import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { displayName, transferTime, type BookingDto, type DaySummaryDto, type DayVehicleDto } from '@bingo/shared';
import { TransferBadge } from '../components/TransferBadge';
import { TransferTime } from '../components/TransferTime';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { addDays, longDate, todayIso } from '../lib/dates';
import { useT } from '../lib/i18n';
import { Calendar } from './Calendar';

function DayTile({ title, date, s }: { title: string; date: string; s: DaySummaryDto | undefined }) {
  const t = useT();
  return (
    <Link to={`/bookings?date=${date}`} className="card tile tile-link tile-compact">
      <span className="when">{t(title)} · {longDate(date)}</span>
      <div className="tile-nums">
        <div><b>{s?.bookings ?? 0}</b><span>{t('bookings')}</span></div>
        <div><b>{s?.pax ?? 0}</b><span>{t('pax')}</span></div>
        <div className={s?.notConfirmed ? 'warn-text' : ''}><b>{s?.notConfirmed ?? 0}</b><span>{t('not CC')}</span></div>
        <div className={s?.missing ? 'warn-text' : ''}><b>{s?.missing ?? 0}</b><span>{t('missing')}</span></div>
      </div>
    </Link>
  );
}

interface PlanDay { dayVehicles: DayVehicleDto[]; bookings: BookingDto[] }
interface Todo { key: string; n: number; text: string; to: string; level: 'bad' | 'warn' }

/**
 * "To do" list of Today: what the office must handle now. Every line links to the place where it is fixed.
 * Computed in the browser from the plan of today and tomorrow, tomorrow's BDR status and the missing-info list.
 */
function useTodo(today: string, tomorrow: string): { todo: Todo[] | null; transfers: { date: string; list: BookingDto[]; vehicles: DayVehicleDto[] }[] } {
  const t = useT();
  const [todo, setTodo] = useState<Todo[] | null>(null);
  const [transfers, setTransfers] = useState<{ date: string; list: BookingDto[]; vehicles: DayVehicleDto[] }[]>([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      const [pToday, pTomorrow, bdr, missing] = await Promise.all([
        api<PlanDay>(`/api/plan?date=${today}`),
        api<PlanDay>(`/api/plan?date=${tomorrow}`),
        api<{ status: Record<string, { state: string }> }>(`/api/plan/bdr/status?date=${tomorrow}`).catch(() => ({ status: {} })),
        api<{ bookings: BookingDto[] }>('/api/bookings/missing'),
      ]);
      const live = (d: PlanDay) => d.bookings.filter((b) => b.status !== 'X');
      const list: Todo[] = [];
      const isT = (b: BookingDto) => !!b.transferDirection;
      const byTime = (a: BookingDto, c: BookingDto) => (transferTime(a) ?? '99').localeCompare(transferTime(c) ?? '99');
      const notCC = live(pTomorrow).filter((b) => b.status !== 'CC').length;
      for (const [label, date, d] of [['Today', today, pToday], ['Tomorrow', tomorrow, pTomorrow]] as const) {
        const unchecked = live(d).filter((b) => isT(b) && !b.expectedChecked).length;
        list.push({ key: `chk-${date}`, n: unchecked, level: label === 'Today' ? 'bad' : 'warn', to: `/transfers?date=${date}`, text: t(label === 'Today' ? 'Today: transfers whose flight or train is not checked' : 'Tomorrow: transfers whose flight or train is not checked') });
      }
      list.push({ key: 'cc', n: notCC, level: 'bad', to: `/bookings?date=${tomorrow}`, text: t('Tomorrow: bookings not confirmed (CC) yet') });
      for (const [label, date, d] of [['Today', today, pToday], ['Tomorrow', tomorrow, pTomorrow]] as const) {
        const unplaced = live(d).filter((b) => !b.dayVehicleId && !isT(b)).length;
        list.push({ key: `place-${date}`, n: unplaced, level: label === 'Today' ? 'bad' : 'warn', to: `/planning?date=${date}`, text: t(label === 'Today' ? 'Today: bookings still to place' : 'Tomorrow: bookings still to place') });
        const unplacedT = live(d).filter((b) => !b.dayVehicleId && isT(b)).length;
        list.push({ key: `tplace-${date}`, n: unplacedT, level: label === 'Today' ? 'bad' : 'warn', to: `/transfers?date=${date}`, text: t(label === 'Today' ? 'Today: transfers still to place' : 'Tomorrow: transfers still to place') });
        const empty = d.dayVehicles.filter((v) => !v.vehicleId || !v.guideId);
        const emptyTours = empty.filter((v) => v.kind !== 'transfers').length;
        const emptyT = empty.length - emptyTours;
        list.push({ key: `veh-${date}`, n: emptyTours, level: label === 'Today' ? 'bad' : 'warn', to: `/planning?date=${date}`, text: t(label === 'Today' ? 'Today: vehicles without van or guide' : 'Tomorrow: vehicles without van or guide') });
        list.push({ key: `tveh-${date}`, n: emptyT, level: label === 'Today' ? 'bad' : 'warn', to: `/transfers?date=${date}`, text: t(label === 'Today' ? 'Today: transfer vehicles without van or driver' : 'Tomorrow: transfer vehicles without van or driver') });
      }
      const bdrTodo = Object.values(bdr.status).filter((s) => s.state === 'none' || s.state === 'outdated').length;
      list.push({ key: 'bdr', n: bdrTodo, level: 'warn', to: `/planning?date=${tomorrow}`, text: t('Tomorrow: BDR not made or out of date') });
      const week = addDays(today, 6);
      const miss = missing.bookings.filter((b) => b.date >= today && b.date <= week).length;
      list.push({ key: 'missing', n: miss, level: 'warn', to: '/bookings/missing', text: t('Next 7 days: bookings missing information') });
      if (alive) {
        setTodo(list.filter((x) => x.n > 0));
        setTransfers([[today, pToday], [tomorrow, pTomorrow]].map(([date, d]) => ({ date: date as string, list: live(d as PlanDay).filter(isT).sort(byTime), vehicles: (d as PlanDay).dayVehicles })));
      }
    })().catch(() => alive && setTodo([]));
    return () => { alive = false; };
  }, [today, tomorrow, t]);
  return { todo, transfers };
}

export function Home() {
  const { me } = useAuth();
  const t = useT();
  const [days, setDays] = useState<DaySummaryDto[]>([]);
  const [missing, setMissing] = useState<number | null>(null);
  const today = todayIso();
  const tomorrow = addDays(today, 1);
  const { todo, transfers } = useTodo(today, tomorrow);

  useEffect(() => {
    api<{ days: DaySummaryDto[] }>(`/api/bookings/summary?from=${today}&to=${tomorrow}`).then((r) => setDays(r.days)).catch(() => undefined);
    api<{ bookings: BookingDto[] }>('/api/bookings/missing').then((r) => setMissing(r.bookings.length)).catch(() => undefined);
  }, [today, tomorrow]);

  const first = me?.user.name.split(' ')[0] ?? '';
  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>{t('Hello {name}', { name: first })}</h1>
          <div className="muted">{longDate(today)}</div>
        </div>
        <Link className="btn btn-primary" to="/bookings">{t('Open bookings')}</Link>
      </div>
      <div className="today-tiles">
        <DayTile title="Today" date={today} s={days.find((d) => d.date === today)} />
        <DayTile title="Tomorrow" date={tomorrow} s={days.find((d) => d.date === tomorrow)} />
        <Link to="/bookings/missing" className={`card tile tile-link tile-compact ${missing ? 'tile-warn' : ''}`}>
          <span className="when">{t('Missing info · from today on')}</span>
          <div className="tile-big"><b>{missing ?? '…'}</b> {missing === 1 ? t('booking to complete') : t('bookings to complete')}</div>
        </Link>
      </div>
      <div className="today-grid">
        <div className="today-main">
          <section className="card today-cal"><Calendar embedded /></section>
          <section className="card today-transfers" aria-label={t('Transfers')}>
            <h2>{t('Transfers')}</h2>
            <div className="today-tr-days">
              {transfers.map((d, i) => (
                <div key={d.date}>
                  <Link to={`/transfers?date=${d.date}`} className="today-tr-title"><b>{t(i === 0 ? 'Today' : 'Tomorrow')}</b> <span className="muted">{longDate(d.date)} · {d.list.length === 1 ? t('1 transfer') : t('{n} transfers', { n: d.list.length })} · {d.list.reduce((a, b) => a + b.pax, 0)} {t('pax')}</span></Link>
                  {d.list.length === 0 ? <div className="muted">{t('No transfer.')}</div> : (
                    <ul className="today-tr-list">
                      {d.list.map((b) => {
                        const v = d.vehicles.find((x) => x.id === b.dayVehicleId);
                        return (
                          <li key={b.id}>
                            <TransferTime b={b} />
                            <TransferBadge d={b.transferDirection!} checked={b.expectedChecked} />
                            <span className="ellipsis">{displayName(b) || b.reference}</span>
                            <span className="muted">{b.pax}</span>
                            <span className="ellipsis muted">{b.pickupPlace || '?'} → {b.dropoffPlace || '?'}</span>
                            <span className={v ? 'today-tr-v' : 'today-tr-v none'}>{v ? `V${v.number}` : t('to place')}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>
        <aside className="card today-todo" aria-label={t('To do')}>
          <h2>{t('To do')}</h2>
          {todo === null ? <div className="muted">{t('Loading…')}</div> : todo.length === 0 ? (
            <div className="muted">{t('Nothing to do for today and tomorrow.')}</div>
          ) : (
            <ul>
              {todo.map((x) => (
                <li key={x.key}>
                  <Link to={x.to} className={`todo-row ${x.level}`}><b>{x.n}</b><span>{x.text}</span></Link>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>
    </div>
  );
}
